import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableRow, TableCell } from "@/components/ui/table";
import {
  useAddPatientAllergy,
  useAllergies,
  useAllergyTypes,
  usePatientAllergies,
  useDeletePatientAllergy,
} from "@/hooks/useConsultation";
import { useToast } from "@/hooks/use-toast";
import { Search, Loader2 } from "lucide-react";
import {
  DismissibleChip,
  ChipContainer,
} from "@/components/consultation/shared/DismissibleChip";
import type { Allergy } from "@/types/appointments";

interface AddAllergyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  /** Pre-select this allergy type when the dialog opens */
  defaultTypeId?: number | null;
}

export const AddAllergyDialog = ({
  open,
  onOpenChange,
  appointmentId,
  defaultTypeId,
}: AddAllergyDialogProps) => {
  const { toast } = useToast();
  const addAllergy = useAddPatientAllergy();
  const deleteAllergy = useDeletePatientAllergy();

  const { data: allergyTypes = [] } = useAllergyTypes();
  const { data: existingAllergies = [] } = usePatientAllergies(appointmentId);

  const [selectedAllergyTypeId, setSelectedAllergyTypeId] = useState<
    string | null
  >(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // When opened from a per-category + button, lock the type selector
  const isTypeLocked = defaultTypeId != null;

  // Reset state when dialog opens, honour defaultTypeId
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setDebouncedQuery("");
      if (defaultTypeId != null) {
        setSelectedAllergyTypeId(String(defaultTypeId));
      } else if (allergyTypes.length > 0) {
        setSelectedAllergyTypeId(String(allergyTypes[0].id));
      }
    }
  }, [open, defaultTypeId, allergyTypes]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: searchResults = [], isLoading: isSearching } =
    useAllergies(debouncedQuery);

  const selectedAllergyType = useMemo(
    () => allergyTypes.find((t) => String(t.id) === selectedAllergyTypeId),
    [allergyTypes, selectedAllergyTypeId]
  );

  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const addedAllergyIds = useMemo(
    () => new Set(existingAllergies.map((a) => a.allergy_info.id)),
    [existingAllergies]
  );

  const filteredSearchResults = useMemo(() => {
    let results = searchResults;
    if (selectedAllergyTypeId) {
      results = results.filter(
        (allergy: Allergy) =>
          String(allergy.allergy_type.id) === selectedAllergyTypeId
      );
    }
    return results.filter(
      (allergy: Allergy) => !addedAllergyIds.has(allergy.id)
    );
  }, [searchResults, selectedAllergyTypeId, addedAllergyIds]);

  const handleAddAllergy = async (allergy: Allergy) => {
    try {
      await addAllergy.mutateAsync({
        appointmentId,
        data: { allergy: allergy.id },
      });
      toast({
        title: "Success",
        description: `"${allergy.name}" added successfully.`,
      });
      setSearchQuery("");
    } catch {
      toast({
        title: "Error",
        description: "Failed to add allergy.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveAllergy = async (
    allergyId: string,
    allergyName: string
  ) => {
    try {
      await deleteAllergy.mutateAsync({ appointmentId, allergyId });
      toast({
        title: "Removed",
        description: `"${allergyName}" removed.`,
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to remove allergy.",
        variant: "destructive",
      });
    }
  };

  // Group existing allergies by type id
  const groupedExisting = useMemo(() => {
    return existingAllergies.reduce(
      (acc, allergy) => {
        const typeId = allergy.allergy_info.allergy_type.id;
        if (!acc[typeId]) acc[typeId] = [];
        acc[typeId].push(allergy);
        return acc;
      },
      {} as Record<number, typeof existingAllergies>
    );
  }, [existingAllergies]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] h-[600px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="z-h2 text-foreground">
            {selectedAllergyType
              ? `Add ${capitalize(selectedAllergyType.name)} Allergy`
              : "Add Allergies"}
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-3" />

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Already Added — tabular, grouped by type */}
          <div className="px-6 py-4 border-b border-border/60 shrink-0">
            <h3 className="z-label text-muted-foreground mb-3">
              Already Added
            </h3>
            <div className="rounded-lg border border-border/60 overflow-hidden max-h-[180px] overflow-y-auto scrollbar-thin">
              {existingAllergies.length > 0 ? (
                <Table>
                  <TableBody>
                    {allergyTypes
                      .filter((type) => type.is_active && !type.is_deleted)
                      .filter((type) =>
                        isTypeLocked
                          ? String(type.id) === selectedAllergyTypeId
                          : true
                      )
                      .map((type) => {
                        const typeAllergies = groupedExisting[type.id];
                        if (!typeAllergies || typeAllergies.length === 0)
                          return null;

                        return (
                          <TableRow
                            key={type.id}
                            className="hover:bg-transparent"
                          >
                            <TableCell className="py-2 px-3 whitespace-nowrap w-0">
                              <span className="z-eyebrow text-muted-foreground">
                                {capitalize(type.name)}
                              </span>
                              <span className="z-hint text-muted-foreground/70 ml-1">
                                ({typeAllergies.length})
                              </span>
                            </TableCell>
                            <TableCell className="py-2 px-3">
                              <ChipContainer>
                                {typeAllergies.map((a) => (
                                  <DismissibleChip
                                    key={a.id}
                                    label={a.allergy_info.name}
                                    className="border-warning/30 bg-warning/10 text-warning"
                                    onRemove={() =>
                                      handleRemoveAllergy(
                                        a.id,
                                        a.allergy_info.name
                                      )
                                    }
                                  />
                                ))}
                              </ChipContainer>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-4 z-body text-muted-foreground">
                  No allergies added yet
                </div>
              )}
            </div>
          </div>

          {/* Search Section */}
          <div className="flex-1 flex flex-col min-h-0 bg-background">
            <div className="p-6 border-b border-border/60 bg-background z-10 space-y-2">
              <div className="flex gap-0">
                <Select
                  value={selectedAllergyTypeId || ""}
                  onValueChange={(value) => {
                    setSelectedAllergyTypeId(value);
                    setSearchQuery("");
                  }}
                  disabled={isTypeLocked}
                >
                  <SelectTrigger
                    className="w-[150px] rounded-r-none border-r-0 bg-muted/30"
                    disabled={isTypeLocked}
                  >
                    <SelectValue placeholder="Select type">
                      {selectedAllergyType
                        ? capitalize(selectedAllergyType.name)
                        : "Select type"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {allergyTypes.map((type) => (
                      <SelectItem key={type.id} value={String(type.id)}>
                        <div className="flex items-center gap-1">
                          {type.icon && (
                            <img
                              src={type.icon}
                              alt={type.name}
                              className="w-4 h-4"
                            />
                          )}
                          <span>{capitalize(type.name)}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Start typing an allergy..."
                    className="pl-9 rounded-l-none bg-muted/50 border-border/60 z-body"
                    autoFocus
                  />
                  {isSearching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                  )}
                </div>
              </div>
            </div>

            {/* Search Results */}
            <div className="flex-1 overflow-y-auto scrollbar-thin p-6">
              {searchQuery.length >= 1 ? (
                filteredSearchResults.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {filteredSearchResults.map((allergy: Allergy) => (
                      <DismissibleChip
                        key={allergy.id}
                        label={allergy.name}
                        onAdd={() => handleAddAllergy(allergy)}
                      />
                    ))}
                  </div>
                ) : isSearching ? (
                  <div className="text-center py-4 z-body text-muted-foreground">
                    Searching...
                  </div>
                ) : (
                  <div className="text-center py-4 z-body text-muted-foreground">
                    No allergies found for{" "}
                    {selectedAllergyType?.name || "this type"}
                  </div>
                )
              ) : (
                <div className="text-center py-8 z-body text-muted-foreground">
                  Start typing to search for allergies
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
